import * as React from 'react';
import { t } from '@reos/i18n';
import {
  fetchAdvertisingPerformance,
  fetchOwnerReportSettings,
  importPortalStatsCsv,
  lastThirtyDaysPeriod,
  OWNER_REPORT_FREQUENCY_CHOICES,
  ownerReportPdfUrl,
  ownerReportPreviewUrl,
  resubscribeOwnerReport,
  saveOwnerReportSettings,
  type AdvertisingPerformanceDto,
  type AdvertisingPerformanceRecommendation,
  type OwnerReportSettingsDto,
} from '../lib/advertising-performance-api.js';
import { formatMoney } from '../lib/properties.js';
import { DomainApiError } from '../lib/api-error.js';

const cs = { locale: 'cs' } as const;

function errorText(cause: unknown): string {
  return cause instanceof DomainApiError ? cause.message : cause instanceof Error ? cause.message : String(cause);
}

/** Jeden sloupec grafu „zobrazeni po dnech" — jedna rada (SR3 ROZHODNUTI-1: prohlidky nejdou k portalu, graf je proto souhrn, ne stoh po portalech). */
function DailyViewsChart({ data }: { readonly data: AdvertisingPerformanceDto }): React.JSX.Element | null {
  const totalsByDate = new Map<string, number>();
  for (const row of data.dailyViews) {
    totalsByDate.set(row.statDate, (totalsByDate.get(row.statDate) ?? 0) + row.views);
  }
  const dates = [...totalsByDate.keys()].sort();
  if (dates.length === 0) return null;
  const max = Math.max(...totalsByDate.values(), 1);
  return (
    <div data-testid="advertising-performance-chart" style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-text-muted)', margin: '0 0 6px' }}>
        {t('webAdmin.caseDetail.advertisingPerformance.chartHeading', cs)}
      </h3>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 64 }}>
        {dates.map((date) => {
          const views = totalsByDate.get(date) ?? 0;
          const heightPct = Math.max(4, Math.round((views / max) * 100));
          return (
            <div
              key={date}
              title={`${date}: ${String(views)}`}
              style={{
                flex: '1 1 auto',
                height: `${String(heightPct)}%`,
                minWidth: 3,
                background: 'var(--rh-accent)',
                borderRadius: '4px 4px 0 0',
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Krok NAP2 bod A5 — DOPORUCENI K INZERCI u zakazky.
 *
 * Odkud se bere: `computeAdvertisingPerformance` (`@reos/finance`, SR3) —
 * tataz `recommendations`, jakou vykresluje HTML report majiteli. Kam tece:
 * jen do teto karty. TEXTY jsou tytez klice jako v reportu
 * (`finance.ownerReport.recommendation*`) — SSOT, zadna druha kopie vety.
 */
function Recommendations({
  rows,
}: {
  readonly rows: readonly AdvertisingPerformanceRecommendation[];
}): React.JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }} data-testid="advertising-performance-recommendations">
      <h3 style={{ fontSize: 'var(--rh-size-footnote)', margin: '0 0 4px' }}>
        {t('webAdmin.caseDetail.advertisingPerformance.recommendationsHeading', cs)}
      </h3>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {rows.map((row) => (
          <li
            key={`${row.portal}:${row.code}`}
            className="rh-a2-meta"
            data-testid={`advertising-performance-recommendation-${row.portal}`}
          >
            {t(
              row.code === 'no_views_despite_spend'
                ? 'finance.ownerReport.recommendationNoViews'
                : 'finance.ownerReport.recommendationNoReaction',
              { ...cs, values: { portal: row.portal } },
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function UploadCsvForm({ caseId, onImported }: { readonly caseId: string; readonly onImported: () => void }): React.JSX.Element {
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState<{ kind: 'success'; count: number } | { kind: 'error'; message: string } | null>(null);

  function onPick(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    setBusy(true);
    setStatus(null);
    void file
      .text()
      .then((csv) => importPortalStatsCsv(csv))
      .then((result) => {
        setStatus({ kind: 'success', count: result.imported });
        onImported();
      })
      .catch((cause: unknown) => setStatus({ kind: 'error', message: errorText(cause) }))
      .finally(() => setBusy(false));
  }

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--rh-rule)', paddingTop: 10 }} data-testid={`advertising-csv-upload-${caseId}`}>
      <h3 style={{ fontSize: 'var(--rh-size-footnote)', margin: '0 0 2px' }}>
        {t('webAdmin.caseDetail.advertisingPerformance.csvHeading', cs)}
      </h3>
      <p className="rh-a2-meta" style={{ margin: '0 0 6px' }}>
        {t('webAdmin.caseDetail.advertisingPerformance.csvHint', cs)}
      </p>
      <label className="rh-a2-btn">
        {t('webAdmin.caseDetail.advertisingPerformance.csvButton', cs)}
        <input type="file" accept=".csv,text/csv" disabled={busy} onChange={onPick} style={{ display: 'none' }} data-testid="advertising-csv-input" />
      </label>
      {status?.kind === 'success' && (
        <p role="status" className="rh-a2-meta" style={{ margin: '8px 0 0' }} data-testid="advertising-csv-success">
          {t('webAdmin.caseDetail.advertisingPerformance.csvSuccess', { ...cs, values: { count: status.count } })}
        </p>
      )}
      {status?.kind === 'error' && (
        <p role="alert" className="rh-a2-error" style={{ margin: '8px 0 0' }} data-testid="advertising-csv-error">
          {t('webAdmin.caseDetail.advertisingPerformance.csvError', { ...cs, values: { error: status.message } })}
        </p>
      )}
    </div>
  );
}


/**
 * Krok SR3b bod 3 — NASTAVENI AUTOMATICKEHO REPORTU MAJITELI.
 *
 * Perioda (7/14/30), zapnuti a NAHLED PRISTIHO ODESLANI. Nahled se tu
 * NEPOCITA — `nextSendAt` prijde hotovy z API (`ownerReportNextSendAt`
 * v @reos/finance), takze admin a planovac nikdy neukazou jiny den.
 *
 * ODHLASENI MAJITELE je silnejsi nez prepinac: kdyz se majitel odhlasil
 * odkazem v reportu, formular to rekne a prepinac report NEOBNOVI — k tomu je
 * zvlastni tlacitko (majitel o to musel rict makleri).
 */
function OwnerReportScheduleForm({ caseId }: { readonly caseId: string }): React.JSX.Element | null {
  const [settings, setSettings] = React.useState<OwnerReportSettingsDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    fetchOwnerReportSettings(caseId).then(
      (loaded) => {
        if (active) setSettings(loaded);
      },
      (cause: unknown) => {
        if (active) setError(errorText(cause));
      },
    );
    return () => {
      active = false;
    };
  }, [caseId]);

  const apply = React.useCallback(
    (next: Promise<OwnerReportSettingsDto>): void => {
      setSaving(true);
      next.then(
        (saved) => {
          setSettings(saved);
          setError(null);
          setSaving(false);
        },
        (cause: unknown) => {
          setError(errorText(cause));
          setSaving(false);
        },
      );
    },
    [],
  );

  if (settings === null && error === null) return null;

  const unsubscribed = settings?.unsubscribedAt != null;

  return (
    <div style={{ marginTop: 16 }} data-testid="owner-report-schedule">
      <h3 style={{ fontSize: 'var(--rh-size-footnote)', margin: '0 0 6px' }}>
        {t('webAdmin.caseDetail.ownerReportSchedule.heading', cs)}
      </h3>

      {error !== null && (
        <p role="alert" className="rh-a2-error" data-testid="owner-report-schedule-error">
          {error}
        </p>
      )}

      {settings !== null && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={settings.autoSend}
              disabled={saving}
              data-testid="owner-report-auto-send"
              onChange={(event) =>
                apply(
                  saveOwnerReportSettings(caseId, {
                    frequencyDays: settings.frequencyDays,
                    autoSend: event.target.checked,
                  }),
                )
              }
            />
            <span>{t('webAdmin.caseDetail.ownerReportSchedule.autoSendLabel', cs)}</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span>{t('webAdmin.caseDetail.ownerReportSchedule.frequencyLabel', cs)}</span>
            <select
              value={settings.frequencyDays}
              disabled={saving}
              data-testid="owner-report-frequency"
              onChange={(event) =>
                apply(
                  saveOwnerReportSettings(caseId, {
                    frequencyDays: Number(event.target.value) as 7 | 14 | 30,
                    autoSend: settings.autoSend,
                  }),
                )
              }
            >
              {OWNER_REPORT_FREQUENCY_CHOICES.map((days) => (
                <option key={days} value={days}>
                  {t('webAdmin.caseDetail.ownerReportSchedule.frequencyOption', {
                    ...cs,
                    values: { days },
                  })}
                </option>
              ))}
            </select>
          </label>

          {/*
            Nahled pristiho odeslani. `null` ma DVA rozdilne duvody a makler
            musi poznat ktery — „vypnuto" si zapne sam, „odhlasil se majitel"
            ne.
          */}
          {unsubscribed ? (
            <div style={{ marginTop: 8 }}>
              <p className="rh-a2-meta" style={{ margin: 0 }} data-testid="owner-report-unsubscribed">
                {t('webAdmin.caseDetail.ownerReportSchedule.unsubscribed', cs)}
              </p>
              <button
                type="button"
                className="rh-a2-btn"
                style={{ marginTop: 6 }}
                disabled={saving}
                data-testid="owner-report-resubscribe"
                onClick={() => apply(resubscribeOwnerReport(caseId))}
              >
                {t('webAdmin.caseDetail.ownerReportSchedule.resubscribeButton', cs)}
              </button>
            </div>
          ) : (
            <p className="rh-a2-meta" style={{ margin: '8px 0 0' }} data-testid="owner-report-next-send">
              {settings.nextSendAt === null
                ? t('webAdmin.caseDetail.ownerReportSchedule.nextSendOff', cs)
                : t('webAdmin.caseDetail.ownerReportSchedule.nextSendAt', {
                    ...cs,
                    values: { date: new Date(settings.nextSendAt).toLocaleDateString('cs-CZ') },
                  })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Krok UI3 — karta „Jak se inzerat dari" v zalozce Inzerce. Data pocita
 * `@reos/finance` (`buildOwnerReportData`, SR3) - karta jen zobrazuje a
 * nabizi import CSV + odkaz na HTML report pro majitele. Prazdny stav se
 * nevymysli (Hulan c): bez dat karta rekne, ze data chybi, ne nulova cisla.
 */
export function AdvertisingPerformanceCard({ caseId }: { readonly caseId: string }): React.JSX.Element {
  const [data, setData] = React.useState<AdvertisingPerformanceDto | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback((): void => {
    const period = lastThirtyDaysPeriod();
    fetchAdvertisingPerformance(caseId, period).then(
      (loadedData) => {
        setData(loadedData);
        setError(null);
        setLoaded(true);
      },
      (cause: unknown) => {
        setError(errorText(cause));
        setLoaded(true);
      },
    );
  }, [caseId]);

  React.useEffect(load, [load]);

  if (!loaded) return <></>;

  const totalViews = data?.traffic.available
    ? data.traffic.data.byPortal.reduce((sum, p) => sum + p.views, 0)
    : 0;
  const totalReactions = data?.reactions.available ? data.reactions.data.total : 0;
  const hasAnyData = data !== null && (data.traffic.available || data.performance.available || data.reactions.available);

  return (
    <section className="rh-a2-card" data-testid="case-advertising-performance">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseDetail.advertisingPerformance.heading', cs)}</h2>
      </div>
      <p className="rh-a2-card__hint">{t('webAdmin.caseDetail.advertisingPerformance.hint', cs)}</p>

      {error !== null && (
        <p role="alert" className="rh-a2-error" data-testid="advertising-performance-error">
          {t('webAdmin.caseDetail.advertisingPerformance.loadError', { ...cs, values: { error } })}
        </p>
      )}

      {data !== null && !hasAnyData && (
        <p className="rh-a2-meta" data-testid="advertising-performance-empty">
          {t('webAdmin.caseDetail.advertisingPerformance.emptyState', cs)}
        </p>
      )}

      {data !== null && hasAnyData && (
        <>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }} data-testid="advertising-performance-summary">
            <div>
              <div className="rh-a2-meta">{t('webAdmin.caseDetail.advertisingPerformance.summaryViews', cs)}</div>
              <strong data-testid="advertising-performance-total-views">{totalViews.toLocaleString('cs-CZ')}</strong>
            </div>
            <div>
              <div className="rh-a2-meta">{t('webAdmin.caseDetail.advertisingPerformance.summaryReactions', cs)}</div>
              <strong data-testid="advertising-performance-total-reactions">{totalReactions.toLocaleString('cs-CZ')}</strong>
            </div>
          </div>

          {data.performance.available ? (
            <table style={{ marginTop: 10, width: '100%', fontSize: 'var(--rh-size-footnote)' }} data-testid="advertising-performance-table">
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--rh-text-muted)' }}>
                  {/* Sloupec "Portál" sdili klic s HTML reportem (finance.ownerReport) - SSOT, zadna druha kopie popisku. */}
                  <th>{t('finance.ownerReport.portalColumnPortal', cs)}</th>
                  <th>{t('webAdmin.caseDetail.advertisingPerformance.summaryCostPerView', cs)}</th>
                  <th>{t('webAdmin.caseDetail.advertisingPerformance.summaryCostPerReaction', cs)}</th>
                </tr>
              </thead>
              <tbody>
                {data.performance.data.byPortal.map((row) => (
                  <tr key={row.portal} data-testid={`advertising-performance-row-${row.portal}`}>
                    <td>{row.portal}</td>
                    <td>{row.costPerView !== null ? formatMoney({ amount: row.costPerView, currency: row.currency }) : '—'}</td>
                    <td>{row.costPerReaction !== null ? formatMoney({ amount: row.costPerReaction, currency: row.currency }) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="rh-a2-meta" style={{ marginTop: 6 }} data-testid="advertising-performance-no-costs">
              {t('webAdmin.caseDetail.advertisingPerformance.noCosts', cs)}
            </p>
          )}

          {data.performance.available && <Recommendations rows={data.performance.data.recommendations} />}

          <DailyViewsChart data={data} />

          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a
              className="rh-a2-btn rh-a2-btn--primary"
              href={ownerReportPreviewUrl(caseId, lastThirtyDaysPeriod())}
              target="_blank"
              rel="noreferrer"
              data-testid="case-owner-report-button"
            >
              {t('webAdmin.caseDetail.advertisingPerformance.reportButton', cs)}
            </a>
            {/* Krok NAP2 bod A6 — PDF z TEZE routy, jakou pouziva planovac (P1EXP3). */}
            <a
              className="rh-a2-btn"
              href={ownerReportPdfUrl(caseId, lastThirtyDaysPeriod())}
              target="_blank"
              rel="noreferrer"
              data-testid="case-owner-report-pdf-button"
            >
              {t('webAdmin.caseDetail.advertisingPerformance.reportPdfButton', cs)}
            </a>
          </div>
        </>
      )}

      <OwnerReportScheduleForm caseId={caseId} />

      <UploadCsvForm caseId={caseId} onImported={load} />
    </section>
  );
}
